import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, TruckIcon, PackageCheck, Timer, ArrowRight, CheckCircle2, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { useLang } from '../context/LanguageContext';
import { IMAGES } from '../mock';

const PageHeader = ({ eyebrow, title, subtitle }) => (
  <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 md:pt-24 pb-14 md:pb-16">
    <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
      <div className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-red-700">
        <span className="w-6 h-px bg-red-500" />{eyebrow}<span className="w-6 h-px bg-red-500" />
      </div>
      <h1 className="mt-5 text-[40px] md:text-[54px] leading-[1.05] font-semibold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="mt-5 text-[16.5px] text-slate-600 max-w-2xl mx-auto leading-relaxed">{subtitle}</p>}
    </div>
  </section>
);

const Services = () => {
  const { t, lang } = useLang();
  const icons = [Zap, TruckIcon, Layers, PackageCheck, Timer];
  const imgs = [IMAGES.expressTruck, IMAGES.cargo, IMAGES.ltlTruck, IMAGES.warehouse, IMAGES.van];

  return (
    <div>
      <SEO page="services" />
      <PageHeader eyebrow={t.services.eyebrow} title={t.services.title} subtitle={t.services.subtitle} />

      <section className="pb-20 md:pb-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 space-y-16 md:space-y-24">
          {t.services.items.map((s, i) => {
            const Icon = icons[i];
            const reverse = i % 2 === 1;
            return (
              <div key={s.id} className={`grid lg:grid-cols-12 gap-8 md:gap-14 items-center ${reverse ? 'lg:[&>div:first-child]:order-2' : ''}`}>
                <div className="lg:col-span-6">
                  <div className="rounded-3xl overflow-hidden ring-1 ring-slate-900/5 shadow-[0_30px_60px_-25px_rgba(15,23,42,0.25)]">
                    <img src={imgs[i]} alt={s.title} className="w-full h-[380px] md:h-[440px] object-cover" loading="lazy" />
                  </div>
                </div>
                <div className="lg:col-span-6">
                  <div className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-red-700">
                    <span className="text-slate-400 font-mono">0{i + 1}</span>
                    <span className="w-6 h-px bg-red-500" />
                    {lang === 'cs' ? 'Služba' : 'Service'}
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                      <Icon size={20} />
                    </div>
                    <h2 className="text-[30px] md:text-[36px] font-semibold tracking-tight text-slate-900">{s.title}</h2>
                  </div>
                  <p className="mt-5 text-[16px] text-slate-600 leading-relaxed">{s.desc}</p>
                  <ul className="mt-6 grid sm:grid-cols-1 gap-2.5">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-[14.5px] text-slate-800">
                        <CheckCircle2 size={17} className="mt-0.5 text-blue-700 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <Link to="/contact">
                      <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5 h-11 text-[14.5px] font-medium">
                        {t.cta.requestQuote}
                        <ArrowRight size={16} className="ml-1.5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Services;
